import { useSession } from '@clerk/clerk-react';
import { ORIGINAL_VIDEO_LANGUAGE_OPTIONS } from '@src/config/languages';
import { DI_TOKENS, resolve } from '@src/di/container';
import { NEW_STORIES_COMPLETION_QUERY } from '@src/gql/story-gql';
import { useUserStoryUploadFlow } from '@src/stores-v2/StoreContext';
import {
  getEnvBucket,
  getExternalUploadsBucketPathKey,
  renameFile,
  uploadFileToS3ExternalUploads,
} from '@src/utility/s3';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { useStoryUploadAnalytics } from './useStoryUploadAnalytics';

import type { SimpleSchemaTypes } from '@datocms/cma-client-browser';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type VideoCreatorStore from '@src/stores/VideoCreatorStore';
import type { DatoClientStore } from '@src/stores-v2/DatoClientStore';
import type { UserIdentityStore } from '@src/stores-v2/UserIdentityStore';
import type { DatoStory, Showcase, Story } from '@src/types.ts/story';

export enum Step {
  one = 1,
  two,
  three,
  four,
  five,
}

const MAX_AWS_UPLOAD_SIZE_GB_NO_LIMIT = 100;
const MAX_AWS_UPLOAD_SIZE_GB_LIMIT = 5;
const MAX_AWS_UPLOAD_SIZE_GB = 2.7;
const MAX_DATO_UPLOAD_SIZE_GB = 1;
const MAX_STORY_TITLE_LENGTH = 120;

export type ArtifactUpload = SimpleSchemaTypes.Upload & { _internalId: string };
export type ArtifactFile = File & { _internalId: string };

export type StoryVideoMusicStrategy = 'keep' | 'process';

type HookReturnType = {
  step: Step;
  goToNextStep: () => void;
  canGoToStep: (s: number) => boolean;
  handleStoryVideoUpload: (f: File[]) => void;
  handleStoryUrlUpload: (url: string) => void;
  handleStoryBRollUpload: (f: File[]) => void;
  /** Resolved low-level upload mechanism for primary story video. */
  uploadMechanism: 'worker' | 'main-thread' | 'legacy' | 'unknown';
  storyName: string;
  setStoryName: (s: string) => void;
  maxStoryTitleLength: number;
  storyLanguage: string | undefined;
  setStoryLanguage: (s: string) => void;
  storyType: 'raw' | 'produced' | null;
  setStoryType: (t: 'raw' | 'produced') => void;
  videoMusicStrategy: StoryVideoMusicStrategy;
  setVideoMusicStrategy: (s: StoryVideoMusicStrategy) => void;
  storyArtifactsFiles: ArtifactFile[];
  storyArtifactsVideoFiles: ArtifactFile[];
  storyArtifacts: ArtifactUpload[];
  storyArtifactsVideo: ArtifactUpload[];
  saveArtifactNotes: (internalId: string) => void;
  setArtifactNotes: (internalId: string, notes: string) => void;
  deleteArtifact: (internalId: string) => void;
  saveArtifactVideoNotes: (internalId: string) => void;
  setArtifactVideoNotes: (internalId: string, notes: string) => void;
  deleteArtifactVideo: (internalId: string) => void;
  maxUploadSizeGB: number;
};

type Params = {
  organization: Showcase;
  videoCreator: VideoCreatorStore | IVideoCreatorStore;
  datoClientStore: DatoClientStore;
  userIdentity: UserIdentityStore;
  onArtifactUploadError?: (kind: 'doc' | 'video', error: unknown) => void;
  storyCreationService?: {
    createBlankStory: (args: Record<string, unknown>) => Promise<DatoStory>;
    startOldVideoProcessingPipeline: (
      storyId: string,
      file?: File | null,
    ) => Promise<void>;
    startProcessingUploadedVideo: (
      args: Record<string, unknown>,
    ) => Promise<void>;
    uploadMultiFiles: (
      files: File[],
      storyId: string,
      orgId?: string,
      clerkToken?: string,
      opts?: Record<string, unknown>,
      preferAwsSdk?: boolean,
    ) => Promise<{ uploadedUrls: string[] }>;
    uploadSingleFile: (
      file: File,
      storyId: string,
      orgId?: string,
      clerkToken?: string,
      opts?: Record<string, unknown>,
      preferAwsSdk?: boolean,
    ) => Promise<void>;
  };
  onStoryBlankCreated: (storyId: string) => void;
  onStoryInitialized: (s: Story) => void;
  onStoryCreated: (s: Story) => void;
  onStoryProgress: (percent: number, storyId?: string) => void;
  onError: (...data: any[]) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
};

export function useStoryCreator(params: Params): HookReturnType {
  const userStoryUploadFlowStore = useUserStoryUploadFlow();
  const { videoCreator } = params;
  const { datoClientStore } = params;
  const { currentRole } = params.userIdentity;
  const {
    storyManagerEnableAwsSdkUploads,
    enableLargeFileSizeUpload,
    enableNoLimitFileSizeUpload,
  } = useFlagsCombination(currentRole);
  const storyTracker = useStoryUploadAnalytics({});
  const storyCreationService =
    params.storyCreationService || resolve(DI_TOKENS.STORY_CREATION_SERVICE);
  const { session: clerkSession } = useSession();

  const [step, setStep] = useState<Step>(1);
  const [story, setStory] = useState<Story>();

  // step 1 data
  const [storyVideoFile, setStoryVideoFile] = useState<File | null>(null);
  const [storyVideoUrl, setStoryVideoUrl] = useState<string | null>(null);
  const blankStoryRef = useRef<DatoStory | null>(null);
  const storyVideoUploadCompleted = useRef<boolean>(false);
  const lastTimeStoryLogUpdatedRef = useRef<number>(0);
  const lastUploadPercentLoggedRef = useRef<number>(0);
  const [uploadedFileUrl, setUploadedFileUrl] = useState<boolean | null>(null);
  const uploadMechanismRef = useRef<
    'worker' | 'main-thread' | 'legacy' | 'unknown'
  >(storyManagerEnableAwsSdkUploads ? 'unknown' : 'legacy');
  const [multiFileVideoUrls, setMultiFileVideoUrls] = useState<string[] | null>(
    null,
  );
  const [isMultiFileUpload, setIsMultiFileUpload] = useState<boolean>(false);
  const [isUrlUpload, setIsUrlUpload] = useState<boolean>(false);
  const urlUploadIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );

  // step 2 data
  const [storyType, setStoryType] = useState<'raw' | 'produced' | null>(null);
  const [videoMusicStrategy, setVideoMusicStrategy] =
    useState<StoryVideoMusicStrategy>('process');

  // step 3 data
  const [storyName, setStoryName] = useState<string>('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const [storyTellerName, _setStoryTellerName] = useState<string>(
    params.organization?.title || 'no_org',
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const [storyTellerTitle, _setStoryTellerTitle] = useState<string>(
    'Organization storyteller',
  );
  const [storyLanguage, setStoryLanguage] = useState<string | undefined>(() => {
    const defaultLanguage =
      videoCreator?.organization?.defaultLanguage || 'en-us';
    return videoCreator?.organization?.allowLangSelectAtUserStoryUpload
      ? (
          ORIGINAL_VIDEO_LANGUAGE_OPTIONS.find(
            o => o.value === defaultLanguage,
          ) || ORIGINAL_VIDEO_LANGUAGE_OPTIONS[0]
        ).value
      : undefined;
  });

  // step 4 data
  const [storyArtifactsFiles, setStoryArtifactsFiles] = useState<
    ArtifactFile[]
  >([]);
  const [storyArtifacts, setStoryArtifacts] = useState<ArtifactUpload[]>([]);
  const [storyArtifactsVideoFiles, setStoryArtifactsVideoFiles] = useState<
    ArtifactFile[]
  >([]);
  const [storyArtifactsVideo, setStoryArtifactsVideo] = useState<
    ArtifactUpload[]
  >([]);
  const [bRollReady, setBRollReady] = useState<boolean>(false);
  const [storyUploadProcessingStarted, setStoryUploadProcessingStarted] =
    useState<boolean>(false);
  const storyArtifactsRef = useRef<ArtifactUpload[]>([]);
  const storyArtifactsFilesRef = useRef<ArtifactFile[]>([]);
  const storyArtifactsVideoRef = useRef<ArtifactUpload[]>([]);
  const storyArtifactsVideoFilesRef = useRef<ArtifactFile[]>([]);
  const lastProgressPercentRef = useRef<number>(0);
  const [nextCallback, setNextCallback] = useState<'init' | 'create'>('init');

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (urlUploadIntervalRef.current) {
        clearInterval(urlUploadIntervalRef.current);
        urlUploadIntervalRef.current = null;
      }
    };
  }, []);

  // Define all functions before useEffects to avoid linting errors
  const getStoryUpdates = async (): Promise<Partial<Story>> => {
    if (!story?.id) return {};
    try {
      const queryResult: {
        allStories: Partial<Story>[];
      } = await datoClientStore.gqlClient.request(
        NEW_STORIES_COMPLETION_QUERY,
        {
          ids: [story.id],
        },
      );
      return queryResult.allStories[0];
    } catch (err) {
      console.warn('Error fetching story updates', err);
      return {};
    }
  };

  const callStoryProgressCbOnceFor = (percent: number, storyId?: string) => {
    if (percent > lastProgressPercentRef.current || percent === 100) {
      lastProgressPercentRef.current = percent;
      params.onStoryProgress(percent, storyId);
    }
  };

  const handleVideoUploadProgress = async (
    progress: number,
    done?: boolean,
  ) => {
    // If blank story not yet created, queue the highest observed progress.
    if (!blankStoryRef.current) {
      return; // Defer logging & callbacks until story exists
    }
    const timestamp = Date.now();
    if (
      !storyVideoUploadCompleted.current &&
      (timestamp - lastTimeStoryLogUpdatedRef.current > 10000 ||
        progress - lastUploadPercentLoggedRef.current > 10 ||
        done)
    ) {
      try {
        lastTimeStoryLogUpdatedRef.current = timestamp;
        lastUploadPercentLoggedRef.current = progress;
        await datoClientStore.storyRepository.updateStoryStepsLog({
          id: blankStoryRef.current.uploadLog,
          initial_upload: JSON.stringify({
            ...(done ? { status: 'success' } : {}),
            progress,
            updatedAt: timestamp,
          }),
        });
      } catch (err) {
        console.warn(
          'Error updating story upload log with video upload progress',
          err,
        );
        lastTimeStoryLogUpdatedRef.current = timestamp - 10000;
        lastUploadPercentLoggedRef.current = progress - 10;
      }
    }
    callStoryProgressCbOnceFor(
      Math.round(progress / 2),
      blankStoryRef.current?.id,
    );
  };

  const formatCurrentUserData = () => {
    const userData = params.userIdentity.getUserIdentity();
    if (!userData) {
      return null;
    }
    const { id, name, email, type } = userData;
    return { id, name, email: email ?? '', type };
  };

  const handleStoryCreate = async () => {
    if (!blankStoryRef.current) {
      setTimeout(() => {
        handleStoryCreate();
      }, 5 * 1000);
      return;
    }

    try {
      const storyUpdate = await videoCreator.updateStory({
        id: blankStoryRef.current.id,
        title: storyName,
        storyTeller: blankStoryRef.current.storyTeller,
        storyType: storyType!,
        originalVideoLanguage: storyLanguage,
      });
      setStory({
        ...blankStoryRef.current,
        ...storyUpdate,
      } as Story);
    } catch (error) {
      params.onError(error);
      storyTracker.analyticsLog(
        'error',
        'Error updating story with name and storyteller',
        { error },
      );
    }
  };

  const createBlankStoryLegacy = async (): Promise<DatoStory> =>
    videoCreator.createStory({
      title: `user-upload-${uuid()}`,
      storyType: storyType || undefined,
      storyTeller: {
        name: storyTellerName,
        id: (
          await videoCreator.upsertStoryTeller({
            name: storyTellerName,
            title: storyTellerTitle,
          })
        ).id,
      },
      primaryShowcase: params.organization,
      externalUploadLog: { uploadedBy: formatCurrentUserData() },
      byExternalUser: true,
    });

  const createBlankStory = async (): Promise<DatoStory> => {
    return storyCreationService.createBlankStory({
      storyType,
      storyTellerName,
      storyTellerTitle,
      organization: params.organization,
      useAws: true,
      userData: formatCurrentUserData(),
    });
  };

  const startOldVideoProcessingPipeline = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/story/upload-task-old`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            storyId: story!.id,
            s3Key: getExternalUploadsBucketPathKey(
              renameFile(storyVideoFile!, story!.id).name,
              params.organization.id,
            ),
          }),
        },
      );
      if (!response.ok) {
        throw new Error(`Not ok: ${response.status}`);
      }
    } catch (err) {
      console.error('Error when triggering old video processing pipeline', err);
      console.warn('Error suppressing');
    }
  };

  const startProcessingUploadedVideoLegacy = async () => {
    try {
      const requestBody: Record<string, unknown> = {
        // eslint-disable-line @typescript-eslint/no-explicit-any
        transactionId: storyTracker.transactionId,
        analyticsContext: {
          ...storyTracker.analyticsContext,
          story: {
            id: story!.id,
            name: storyName,
          },
        },
        storyId: story!.id,
        showcaseId: params.organization.id,
        userData: formatCurrentUserData(),
        audioProcessPreset:
          videoMusicStrategy === 'process'
            ? storyType === 'produced'
              ? 'REMOVE_MUSIC'
              : 'CORRECT_AUDIO'
            : undefined,
        // Default to single Rev.ai provider for now
        // Can be configured based on organization settings or user preference
        transcriptionProviders: ['rev', 'aws', 'assembly'],
      };

      if (isUrlUpload && storyVideoUrl) {
        // URL upload - send the source URL for Lambda to fetch
        requestBody.sourceUrl = storyVideoUrl;
        requestBody.s3Key = `sessions/${story!.id}/videos/original.mp4`;

        // Check if this is a Zoom recording with a password
        const zoomPassword = sessionStorage.getItem('zoom_recording_password');
        if (zoomPassword) {
          requestBody.platformPassword = zoomPassword;
          // Clear the password after using it
          sessionStorage.removeItem('zoom_recording_password');
        }
      } else if (isMultiFileUpload && multiFileVideoUrls) {
        // Multiple files - send URLs for concatenation
        requestBody.videoUrls = multiFileVideoUrls;
        requestBody.isMultiFileConcat = true;
      } else {
        // Single file - send S3 key
        requestBody.s3Key = getExternalUploadsBucketPathKey(
          renameFile(storyVideoFile!, story!.id).name,
          videoCreator.organization?.id,
        );
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/story/upload-task`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        },
      );
      if (!response.ok) {
        throw new Error(`Not ok: ${response.status}`);
      }

      // Clear the upload state after successful API call
      setIsMultiFileUpload(false);
      setMultiFileVideoUrls(null);
      setIsUrlUpload(false);
      setStoryVideoUrl(null);
    } catch (error) {
      params.onError(error);
      storyTracker.analyticsLog('error', 'Error calling video processing API', {
        error,
      });
    }
    if (process.env.REACT_APP_ENABLE_OLD_DATO_PROCESSING === 'true') {
      console.debug(
        'REACT_APP_ENABLE_OLD_DATO_PROCESSING',
        process.env.REACT_APP_ENABLE_OLD_DATO_PROCESSING === 'true',
      );
      if (videoMusicStrategy !== 'process') {
        await startOldVideoProcessingPipeline();
      }
    }
  };

  const startProcessingUploadedVideo = async () => {
    if (!storyManagerEnableAwsSdkUploads) {
      return startProcessingUploadedVideoLegacy();
    }
    if (!story) {
      console.warn('No story to process uploaded video for');
      return;
    }
    if (!storyType) {
      console.warn('No story type to process uploaded video for');
      return;
    }
    try {
      await storyCreationService.startProcessingUploadedVideo({
        storyId: story.id,
        storyName,
        storyType,
        storyVideoUrl,
        isUrlUpload,
        isMultiFileUpload,
        multiFileVideoUrls,
        storyVideoFile: storyVideoFile,
        organizationId: videoCreator.organization?.id,
        videoMusicStrategy,
        tracker: storyTracker, // transitional
      });
      setIsMultiFileUpload(false);
      setMultiFileVideoUrls(null);
      setIsUrlUpload(false);
      setStoryVideoUrl(null);
    } catch (error) {
      params.onError(error);
    }
    if (
      process.env.REACT_APP_ENABLE_OLD_DATO_PROCESSING === 'true' &&
      videoMusicStrategy !== 'process'
    ) {
      await startOldVideoProcessingPipeline();
    }
  };

  // option to remove music was not selected - proceed with regular video upload
  useEffect(() => {
    if (
      story &&
      uploadedFileUrl &&
      videoMusicStrategy &&
      !storyVideoUploadCompleted.current
    ) {
      storyVideoUploadCompleted.current = true;
      (async () => {
        await startProcessingUploadedVideo();
      })();
      setStoryUploadProcessingStarted(true);
      userStoryUploadFlowStore.startProcessing(blankStoryRef.current?.id);
    }
  }, [uploadedFileUrl, story, storyType, videoMusicStrategy]);

  useEffect(() => {
    if (step === Step.four && !story) {
      handleStoryCreate();
    }
    if (step === Step.five && story?.id && nextCallback === 'init') {
      // lastProgressPercentRef.current = Infinity; // stop progress updates after story is initialized
      (async () => {
        params.onStoryInitialized({
          ...story,
          ...(await getStoryUpdates()),
        });
        setNextCallback('create');
      })();
    }
  }, [step, story?.id, nextCallback]);

  useEffect(() => {
    console.debug('step', step, storyUploadProcessingStarted, nextCallback);
    if (
      step === Step.five &&
      story &&
      storyUploadProcessingStarted &&
      bRollReady &&
      nextCallback === 'create'
    ) {
      (async () => {
        const created = { ...story, ...(await getStoryUpdates()) };
        params.onStoryCreated(created);
        userStoryUploadFlowStore.markComplete(created.id);
      })();
    }
  }, [step, story, storyUploadProcessingStarted, bRollReady, nextCallback]);

  useEffect(() => {
    console.log('storyArtifacts changed:', storyArtifacts);
    // to prevent methods' closure from capturing stale state
    storyArtifactsRef.current = storyArtifacts;
    storyArtifactsFilesRef.current = storyArtifactsFiles;
    storyArtifactsVideoRef.current = storyArtifactsVideo;
    storyArtifactsVideoFilesRef.current = storyArtifactsVideoFiles;

    // b-roll is uploaded when story artifacts' length matches files' length
    if (
      storyArtifacts.length === storyArtifactsFiles.length &&
      storyArtifactsVideo.length === storyArtifactsVideoFiles.length
    ) {
      // if we are at the last step, time to update story with uploaded b-roll and mark b-roll as ready
      // doing it at the last step makes it less error prone, because b-roll can change multiple times at step 4
      if (story && step === Step.five) {
        (async () => {
          if (
            storyArtifactsFiles.length > 0 ||
            storyArtifactsVideoFiles.length > 0
          ) {
            try {
              const storyUpdate = await videoCreator.updateStory({
                id: story.id,
                storyArtifacts: storyArtifacts.map(a => ({
                  id: a.id,
                })),
                storyArtifactsVideo: storyArtifactsVideo.map(a => ({
                  id: a.id,
                })),
              });
              setStory({
                ...story,
                ...storyUpdate,
              });
            } catch (error) {
              storyTracker.analyticsLog(
                'error',
                'Error updating story with artifacts',
                { error },
              );
            }
          }
          setBRollReady(true);
        })();
      }
      // initial b-roll upload is done - time to update progress, prematurely
      // TODO TEMP disabled, needs to be revisited
      // if (
      //   storyArtifactsFiles.length > 0 ||
      //   storyArtifactsVideoFiles.length > 0 ||
      //   step === Step.five
      // ) {
      //   callStoryProgressCbOnceFor(50);
      // }
    }
  }, [
    storyArtifacts,
    storyArtifactsFiles,
    storyArtifactsVideo,
    storyArtifactsVideoFiles,
    step,
    story?.id,
  ]);

  const goToNextStep = () => {
    setStep(step + 1);
  };

  const handleStoryVideoUploadLegacy = async (files: File[]) => {
    uploadMechanismRef.current = 'legacy';
    if (!files.length) {
      console.warn('No files at AddStoriesModal handleStoryVideoUpload');
      return;
    }

    const isMultipleFiles = files.length > 1;
    const file = files[0];

    setStoryVideoFile(file);
    handleVideoUploadProgress(1);
    goToNextStep();

    storyTracker.trackInit({
      fileSize: isMultipleFiles
        ? files.reduce((sum, f) => sum + f.size, 0)
        : file.size,
      fileType: file.type,
      isConcat: isMultipleFiles,
      fileCount: files.length,
    });

    try {
      blankStoryRef.current = await createBlankStory();
      storyTracker.analyticsLog('info', 'New story created', {
        story: {
          id: blankStoryRef.current.id,
        },
        isConcat: isMultipleFiles,
        fileCount: files.length,
      });
      params.onStoryBlankCreated(blankStoryRef.current.id);

      if (isMultipleFiles) {
        // Multiple files - need to upload all and store for concatenation
        console.log(`Uploading ${files.length} files for concatenation`);

        // Set state for multi-file upload
        setIsMultiFileUpload(true);

        // Upload all files in parallel with smart progress tracking
        const uploadProgressMap = new Map<number, number>();
        files.forEach((_, index) => uploadProgressMap.set(index, 0));

        const calculateOverallProgress = () => {
          const totalProgress = Array.from(uploadProgressMap.values()).reduce(
            (sum, progress) => sum + progress,
            0,
          );
          return (totalProgress / files.length) * 0.5; // Scale to 0-50%
        };

        const uploadPromises = files.map(async (currentFile, i) => {
          const fileKey = getExternalUploadsBucketPathKey(
            `${blankStoryRef?.current?.id}_part_${i}`,
            videoCreator.organization?.id,
          );

          const renamedFile = new File(
            [currentFile],
            `${blankStoryRef?.current?.id}_part_${i}`,
            { type: currentFile.type },
          );

          await uploadFileToS3ExternalUploads(
            renamedFile,
            videoCreator.organization?.id,
            {
              onUploadProgress: progress => {
                uploadProgressMap.set(i, progress);
                const overallProgress = calculateOverallProgress();
                handleVideoUploadProgress(overallProgress);
              },
            },
          );

          return `s3://${getEnvBucket()}/${fileKey}`;
        });

        const uploadedUrls = await Promise.all(uploadPromises);

        // Store the uploaded URLs in state
        setMultiFileVideoUrls(uploadedUrls);

        handleVideoUploadProgress(50); // All files uploaded
        setUploadedFileUrl(true);
      } else {
        // Single file - normal upload flow
        const tBegin = performance.now();
        await uploadFileToS3ExternalUploads(
          renameFile(file, blankStoryRef.current.id),
          videoCreator.organization?.id,
          {
            onUploadProgress: (progress, done, total) => {
              console.debug('Upload file progress', progress, done, total);
              handleVideoUploadProgress(progress, done);
              if (done) {
                storyTracker.analyticsLog(
                  'info',
                  'Story video upload completed',
                  {
                    bytesUploaded: total,
                    secondsElapsed: (performance.now() - tBegin) / 1000,
                  },
                );
                setUploadedFileUrl(true);
              }
            },
          },
        );
      }
    } catch (error) {
      storyTracker.analyticsLog(
        'error',
        'Story creation or video upload failed',
        {
          error,
        },
      );
      params.onError(error);
    }
  };

  /**
   * Handle initial user-selected video file(s).
   * Side-effects:
   *  - Creates blank story (async)
   *  - Initiates upload(s) (single or multi-concat)
   *  - Advances wizard to Step.two immediately (progress updates async)
   *  - Records analytics init event
   */
  const handleStoryVideoUpload = async (files: File[]) => {
    if (!storyManagerEnableAwsSdkUploads) {
      return handleStoryVideoUploadLegacy(files);
    }

    const clerkToken = await clerkSession?.getToken({ skipCache: true });

    if (!files.length) {
      console.warn('No files at AddStoriesModal handleStoryVideoUpload');
      return;
    }

    const isMultipleFiles = files.length > 1;
    const file = files[0];

    setStoryVideoFile(file);
    handleVideoUploadProgress(1);
    goToNextStep();

    storyTracker.trackInit({
      fileSize: isMultipleFiles
        ? files.reduce((sum, f) => sum + f.size, 0)
        : file.size,
      fileType: file.type,
      isConcat: isMultipleFiles,
      fileCount: files.length,
    });

    try {
      blankStoryRef.current = await (storyManagerEnableAwsSdkUploads
        ? createBlankStory()
        : createBlankStoryLegacy());
      storyTracker.analyticsLog('info', 'New story created', {
        story: {
          id: blankStoryRef.current.id,
        },
        isConcat: isMultipleFiles,
        fileCount: files.length,
      });
      params.onStoryBlankCreated(blankStoryRef.current.id);

      // Set state for multi-file upload
      if (isMultipleFiles) {
        setIsMultiFileUpload(true);
        const result = await storyCreationService.uploadMultiFiles(
          files,
          blankStoryRef.current.id,
          videoCreator.organization?.id,
          clerkToken || undefined,
          {
            onProgress: (p: number, done?: boolean) =>
              handleVideoUploadProgress(p, done),
            onUploaded: () => setUploadedFileUrl(true),
            onError: (err: unknown) => params.onError(err),
          },
          storyManagerEnableAwsSdkUploads,
          mechanism => {
            uploadMechanismRef.current = mechanism;
          },
        );
        setMultiFileVideoUrls(result.uploadedUrls);
      } else {
        await storyCreationService.uploadSingleFile(
          file,
          blankStoryRef.current.id,
          videoCreator.organization?.id,
          clerkToken || undefined,
          {
            onProgress: (p: number, done?: boolean) =>
              handleVideoUploadProgress(p, done),
            onUploaded: () => setUploadedFileUrl(true),
            onError: (err: unknown) => params.onError(err),
          },
          storyManagerEnableAwsSdkUploads,
          mechanism => {
            uploadMechanismRef.current = mechanism;
          },
        );
      }

      handleVideoUploadProgress(100); // All files uploaded
      setUploadedFileUrl(true);
    } catch (error) {
      storyTracker.analyticsLog(
        'error',
        'Story creation or video upload failed',
        {
          error,
        },
      );
      params.onError(error);
    }
  };

  const handleStoryUrlUpload = async (url: string) => {
    console.log('handleStoryUrlUpload', url);

    setStoryVideoUrl(url);
    setIsUrlUpload(true);
    handleVideoUploadProgress(1);
    goToNextStep();

    storyTracker.trackInit({
      fileSize: 0, // Unknown for URL uploads
      fileType: 'video/mp4', // Assume MP4 for URLs
      isMultiFileUpload: false,
      isUrlUpload: true,
      sourceUrl: url,
    });

    try {
      // Create blank story with initial metadata
      console.log('Creating Blank Story for URL upload');
      const initialMetadata = {
        sourceUrl: url,
        uploadType: 'url',
        initiatedAt: new Date().toISOString(),
        status: 'initiated',
      };

      // Extract filename from URL for title if possible
      let urlTitle = `url-upload-${uuid()}`;
      try {
        const urlObj = new URL(url);
        const pathname = urlObj.pathname;
        const filename = pathname.split('/').pop();
        if (filename && filename !== '') {
          urlTitle = filename.replace(/\.[^/.]+$/, ''); // Remove file extension
        }
      } catch (e) {
        // Keep default title if URL parsing fails
      }

      const storyTemplate = await videoCreator.createStory({
        title: urlTitle,
        storyType: storyType || undefined,
        storyTeller: {
          name: storyTellerName,
          id: (
            await videoCreator.upsertStoryTeller({
              name: storyTellerName,
              title: storyTellerTitle,
            })
          ).id,
        },
        primaryShowcase: params.organization,
        externalUploadLog: initialMetadata,
        externalUploadStatus: 'IN PROGRESS',
        byExternalUser: true,
      });
      blankStoryRef.current = storyTemplate;
      params.onStoryBlankCreated(storyTemplate.id);

      // Set flags for URL upload
      setIsUrlUpload(true);

      // Start with initial progress
      handleVideoUploadProgress(5);

      // For URL uploads, immediately trigger the step function
      // The Lambda will handle the download asynchronously
      setUploadedFileUrl(true);

      // Clear any existing interval first
      if (urlUploadIntervalRef.current) {
        clearInterval(urlUploadIntervalRef.current);
      }

      // Poll DatoCMS for real progress updates
      urlUploadIntervalRef.current = setInterval(async () => {
        try {
          // Fetch the story to get the latest external_upload_log
          const updatedStory = await datoClientStore.storyRepository?.findOne(
            storyTemplate.id,
          );

          if (updatedStory?.externalUploadLog) {
            const uploadLog =
              typeof updatedStory.externalUploadLog === 'string'
                ? JSON.parse(updatedStory.externalUploadLog)
                : updatedStory.externalUploadLog;

            // Update progress based on real data from Lambda
            if (uploadLog.progress !== undefined) {
              handleVideoUploadProgress(uploadLog.progress);

              // If completed, clean up and mark as done
              if (
                uploadLog.fetchStatus === 'completed' ||
                uploadLog.progress >= 100
              ) {
                if (urlUploadIntervalRef.current) {
                  clearInterval(urlUploadIntervalRef.current);
                  urlUploadIntervalRef.current = null;
                }
                setUploadedFileUrl(true);
                handleVideoUploadProgress(100, true); // ensure final event
                storyTracker.analyticsLog('info', 'URL upload completed', {
                  storyId: storyTemplate.id,
                  url,
                  sizeMB: uploadLog.fileMetadata?.sizeMB,
                });
              }
            }
          }
        } catch (error) {
          console.error('Error polling for upload progress:', error);
        }
      }, 2000); // Poll every 2 seconds

      // Cleanup polling after 5 minutes (just for cleanup, doesn't affect processing)
      setTimeout(() => {
        if (urlUploadIntervalRef.current) {
          clearInterval(urlUploadIntervalRef.current);
          urlUploadIntervalRef.current = null;
          // Fallback to mark as complete if polling fails
          if (lastProgressPercentRef.current < 100) {
            setUploadedFileUrl(true);
            handleVideoUploadProgress(100, true);
          }
        }
      }, 300000); // 5 minute timeout for polling cleanup only

      storyTracker.analyticsLog('info', 'URL upload initiated', {
        storyId: storyTemplate.id,
        url,
      });
    } catch (error) {
      storyTracker.analyticsLog(
        'error',
        'Story creation for URL upload failed',
        {
          error,
          url,
        },
      );
      params.onError(error);
    }
  };

  const handleStoryBRollUpload = async (files: File[]) => {
    console.log('handleStoryBRollUpload', files);
    const imageFiles = files.filter((f): f is ArtifactFile =>
      f.type.includes('image'),
    );
    imageFiles.forEach(f => {
      f._internalId = uuid(); // eslint-disable-line no-param-reassign
    });

    const videoFiles = files.filter((f): f is ArtifactFile =>
      f.type.includes('video'),
    );
    videoFiles.forEach(f => {
      f._internalId = uuid(); // eslint-disable-line no-param-reassign
    });

    let newStoryArtifactsFiles =
      storyArtifactsFilesRef.current.concat(imageFiles);
    setStoryArtifactsFiles(newStoryArtifactsFiles);
    let newStoryArtifactsVideoFiles =
      storyArtifactsVideoFilesRef.current.concat(videoFiles);
    setStoryArtifactsVideoFiles(newStoryArtifactsVideoFiles);

    let newStoryArtifacts = [...storyArtifactsRef.current];

    const imageUploadPromises = imageFiles.map(async file => {
      try {
        const upload =
          await datoClientStore.datoClient?.uploads.createFromFileOrBlob({
            fileOrBlob: file,
            filename: file.name,
          });
        if (upload) {
          return {
            ...upload,
            _internalId: file._internalId,
          };
        }
        return null;
      } catch (err) {
        storyTracker.analyticsLog('warn', 'Error uploading image b-roll', {
          error: err,
        });
        params.onArtifactUploadError?.('doc', err);
        return { error: true, _internalId: file._internalId };
      }
    });

    const imageResults = await Promise.all(imageUploadPromises);

    imageResults.forEach(result => {
      if (!result) return; // skip nulls
      if ('error' in result) {
        // Remove failed upload placeholder from files list
        newStoryArtifactsFiles = newStoryArtifactsFiles.filter(
          f => f._internalId !== result._internalId,
        );
      } else {
        // Successful upload
        newStoryArtifacts = newStoryArtifacts.concat(result);
      }
    });

    setStoryArtifacts(newStoryArtifacts);
    setStoryArtifactsFiles(newStoryArtifactsFiles);
    console.log('handleStoryBRollUpload setStoryArtifacts', newStoryArtifacts);

    let newStoryArtifactsVideo = [...storyArtifactsVideoRef.current];

    const videoUploadPromises = videoFiles.map(async file => {
      try {
        const upload =
          await datoClientStore.datoClient?.uploads.createFromFileOrBlob({
            fileOrBlob: file,
            filename: file.name,
          });
        if (upload) {
          return {
            ...upload,
            _internalId: file._internalId,
          };
        }
        return null;
      } catch (err) {
        storyTracker.analyticsLog('warn', 'Error uploading video b-roll', {
          error: err,
        });
        params.onArtifactUploadError?.('video', err);
        return { error: true, _internalId: file._internalId };
      }
    });

    const videoResults = await Promise.all(videoUploadPromises);

    videoResults.forEach(result => {
      if (!result) return;
      if ('error' in result) {
        newStoryArtifactsVideoFiles = newStoryArtifactsVideoFiles.filter(
          f => f._internalId !== result._internalId,
        );
      } else {
        newStoryArtifactsVideo = newStoryArtifactsVideo.concat(result);
      }
    });

    setStoryArtifactsVideo(newStoryArtifactsVideo);
    setStoryArtifactsVideoFiles(newStoryArtifactsVideoFiles);
  };

  const canGoToStep = (nextStep: number): boolean => {
    if (nextStep === Step.two) {
      return !!storyVideoFile;
    }
    if (nextStep === Step.three) {
      return !!storyType;
    }
    if (nextStep === Step.four) {
      return (
        !!storyName &&
        !!(storyTellerName || '').trim() &&
        storyName.length <= MAX_STORY_TITLE_LENGTH
      );
    }
    if (nextStep === Step.five) {
      return !!story;
    }
    return true;
  };

  const saveArtifactNotes = async (internalId: string): Promise<void> => {
    const artifact = storyArtifactsRef.current.find(
      a => a._internalId === internalId,
    );
    await doSaveArtifactNotes(artifact);
  };

  const doSaveArtifactNotes = async (artifact?: ArtifactUpload) => {
    if (!artifact) {
      return;
    }
    try {
      await datoClientStore.assetRepository?.update(artifact.id, {
        title: artifact.notes || '',
        alt: artifact.notes || '',
      });
    } catch (err) {
      storyTracker.analyticsLog('warn', 'Error saving b-roll notes', {
        error: err,
      });
    }
  };

  const setArtifactNotes = (internalId: string, notes: string): void => {
    setStoryArtifacts(prev =>
      prev.map(a => (a._internalId === internalId ? { ...a, notes } : a)),
    );
  };

  const deleteArtifact = async (internalId: string): Promise<void> => {
    const artifact = storyArtifactsRef.current.find(
      a => a._internalId === internalId,
    );
    if (!artifact) {
      return;
    }

    setStoryArtifacts(
      storyArtifactsRef.current.filter(a => a._internalId !== internalId),
    );
    setStoryArtifactsFiles(
      storyArtifactsFilesRef.current.filter(f => f._internalId !== internalId),
    );

    try {
      await datoClientStore.assetRepository?.delete(artifact.id);
    } catch (err) {
      storyTracker.analyticsLog('warn', 'Error deleting b-roll', {
        error: err,
      });
    }
  };

  const saveArtifactVideoNotes = async (internalId: string): Promise<void> => {
    const artifact = storyArtifactsVideoRef.current.find(
      a => a._internalId === internalId,
    );
    await doSaveArtifactNotes(artifact);
  };

  const setArtifactVideoNotes = (internalId: string, notes: string): void => {
    setStoryArtifactsVideo(
      storyArtifactsVideoRef.current.map(a =>
        a._internalId === internalId ? { ...a, notes } : a,
      ),
    );
  };

  const deleteArtifactVideo = async (internalId: string): Promise<void> => {
    const artifact = storyArtifactsVideoRef.current.find(
      a => a._internalId === internalId,
    );
    if (!artifact) {
      return;
    }

    setStoryArtifactsVideo(
      storyArtifactsVideoRef.current.filter(a => a._internalId !== internalId),
    );
    setStoryArtifactsVideoFiles(
      storyArtifactsVideoFilesRef.current.filter(
        f => f._internalId !== internalId,
      ),
    );

    try {
      await datoClientStore.assetRepository?.delete(artifact.id);
    } catch (err) {
      storyTracker.analyticsLog('warn', 'Error deleting b-roll', {
        error: err,
      });
    }
  };

  return {
    step,
    goToNextStep,
    canGoToStep,
    handleStoryVideoUpload,
    handleStoryUrlUpload,
    handleStoryBRollUpload,
    uploadMechanism: uploadMechanismRef.current,
    storyName,
    setStoryName,
    storyLanguage,
    setStoryLanguage,
    storyType,
    setStoryType,
    videoMusicStrategy,
    setVideoMusicStrategy,
    storyArtifactsFiles,
    storyArtifactsVideoFiles,
    storyArtifacts,
    storyArtifactsVideo,
    saveArtifactNotes,
    setArtifactNotes,
    deleteArtifact,
    saveArtifactVideoNotes,
    setArtifactVideoNotes,
    deleteArtifactVideo,
    maxUploadSizeGB: storyManagerEnableAwsSdkUploads
      ? enableNoLimitFileSizeUpload
        ? MAX_AWS_UPLOAD_SIZE_GB_NO_LIMIT
        : enableLargeFileSizeUpload
          ? MAX_AWS_UPLOAD_SIZE_GB_LIMIT
          : MAX_AWS_UPLOAD_SIZE_GB
      : MAX_DATO_UPLOAD_SIZE_GB,
    maxStoryTitleLength: MAX_STORY_TITLE_LENGTH,
  };
}
